// @flow

import React from 'react';
import styled from 'styled-components';
import { imageUrl } from 'utils/filestack';
import Box from 'components/Box';
import Img, { FilestackImage } from 'components/Image';
import { TrackedConditionalLink } from 'components/Link';
import { H2, Body, BodySmall } from 'components/Text';
import type { ImageBlockData } from 'api/cms';
import { colors } from '../../theme';

const GifImage = styled(Img)`
  max-height: 500px;
  max-width: 100%;
  width: auto;
  ${({ $fullWidthLayout }) =>
    $fullWidthLayout &&
    `
    display: block;
    margin: 0 auto;
    height: auto;
    width: auto;
  `}
  @supports (object-fit: contain) {
    object-fit: contain;
    width: 100%;
    ${({ $fullWidthLayout }) =>
      !$fullWidthLayout &&
      `
      object-position: 0 50%;
    `}
  }
`;

const ImageContainer = styled.div`
  img {
    display: block;
    width: 100%;
    ${({ fullWidthLayout }) =>
      fullWidthLayout &&
      `
      margin: 0 auto;
    `}
  }
`;

const ImageBlock = ({
  data: { headline, subtitle, image, width, url },
  className,
  fullWidthLayout,
  blockIndex,
  pageUpdated
}: {
  data: ImageBlockData,
  className: string,
  fullWidthLayout?: boolean,
  blockIndex: number,
  pageUpdated: string
}) => {
  const { caption, altText } = image;
  const isGif = image.mimetype === 'image/gif';
  return (
    <Box
      as="figure"
      m={0}
      w={1}
      fullWidthLayout={fullWidthLayout}
      className={className}
      data-qa-id="cms-image"
      data-qa-content-width={width.toLowerCase()}
    >
      {(headline || subtitle) && (
        <Box as="figcaption" mb={48}>
          {headline && (
            <H2 textAlign={fullWidthLayout ? 'center' : 'left'}>{headline}</H2>
          )}
          {subtitle && (
            <Body
              textAlign={fullWidthLayout ? 'center' : 'left'}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          )}
        </Box>
      )}
      <Box as={headline || subtitle ? 'figure' : 'div'} m={0}>
        <TrackedConditionalLink
          link={url}
          eventName="CMS Block"
          eventProperties={{
            block_position: blockIndex,
            block_type: 'Image',
            cta_type: 'Image',
            page_updated: pageUpdated
          }}
          aria-label={altText || getText('Follow image link')}
        >
          {isGif ? (
            <GifImage
              src={imageUrl({ handle: image.handle })}
              alt={altText || ''}
              $fullWidthLayout={fullWidthLayout}
              webp
            />
          ) : (
            <ImageContainer>
              <FilestackImage data={image} alt={altText || ''} width={1152} />
            </ImageContainer>
          )}
        </TrackedConditionalLink>
        {caption && (
          <BodySmall color={colors.battleshipGrey} mt="8px" as="figcaption">
            {caption}
          </BodySmall>
        )}
      </Box>
    </Box>
  );
};

export default ImageBlock;
